import React, { useState, useEffect } from "react";
import tw, { styled } from "twin.macro";
import { Star, Loader2, User } from "lucide-react";

// ========================
// STYLED COMPONENTS
// ========================

const Section = tw.section`py-16 bg-white`;
const HeadingContainer = tw.div`text-center mb-10`;
const Subheading = tw.h3`text-[#01325b] tracking-wider uppercase font-semibold text-sm`;
const Heading = tw.h1`text-4xl md:text-5xl font-bold mt-2`;
const Paragraph = tw.p`max-w-3xl mx-auto mt-6 text-gray-700 text-lg leading-relaxed`;
const VectorImg = tw.img`w-32 mx-auto mt-4 opacity-75`;

const RowWrapper = tw.div`w-full mx-auto max-w-[1400px] overflow-hidden relative my-8`;

const LeftFade = tw.div`
  absolute left-0 top-0 h-full w-20 
  bg-gradient-to-r from-white to-transparent z-10 pointer-events-none
`;

const RightFade = tw.div`
  absolute right-0 top-0 h-full w-20 
  bg-gradient-to-l from-white to-transparent z-10 pointer-events-none
`;

// UPDATED: Added hover pause logic here
const MarqueeInner = styled.div(({ reverse }) => [
  tw`flex min-w-[200%] pt-10 pb-5 cursor-default`,
  {
    animation: `marqueeScroll 40s linear infinite`,
    animationDirection: reverse ? "reverse" : "normal",
    // This stops the animation when ANY part of the row is hovered
    "&:hover": {
      animationPlayState: "paused",
    },
  },
]);

const Card = styled.div`
  ${tw`px-5 py-6 rounded-2xl mx-4 shadow bg-white border border-gray-200 
       hover:shadow-xl hover:border-indigo-200 transition-all duration-300 
       w-[380px] h-auto flex-shrink-0 flex flex-col justify-between`}
`;

const CardHeader = tw.div`flex gap-4 items-center`;
const CardImage = tw.img`w-16 h-16 rounded-full object-cover border border-gray-100`;
const AvatarPlaceholder = tw.div`w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-xl`;
const CardName = tw.p`font-semibold text-gray-900 text-lg leading-tight`;
const CardDate = tw.p`text-xs text-gray-500`;
const StarsContainer = tw.div`flex mt-1`;
const CardText = tw.p`text-sm py-3 text-gray-700 leading-relaxed italic  overflow-hidden`;

// ========================
// MAIN COMPONENT
// ========================

const TestimonialsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGoogleReviews();
  }, []);

  const fetchGoogleReviews = async () => {
    try {
      const response = await fetch(
        "https://cavijenderaggarwal.com/reviews.php"
      );
      const data = await response.json();

      if (data && data.reviews) {
        const formattedReviews = data.reviews.map((review) => ({
          name: review.authorAttribution?.displayName || "Google User",
          image: review.authorAttribution?.photoUri || null,
          text:
            review.text?.text ||
            review.originalText?.text ||
            "No review text provided.",
          date: review.relativePublishTimeDescription || "Recently",
          rating: review.rating || 5,
        }));
        setReviews(formattedReviews);
      }
      setLoading(false);
    } catch (err) {
      console.error("Error fetching reviews:", err);
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        size={14}
        fill={i < Math.floor(rating) ? "#fbbc04" : "none"}
        stroke={i < Math.floor(rating) ? "#fbbc04" : "#ddd"}
      />
    ));
  };

  const CreateCard = ({ card }) => {
    const [imgError, setImgError] = useState(false);

    return (
      <Card>
        <div>
          <CardHeader>
            {card.image && !imgError ? (
              <CardImage
                src={card.image}
                alt={card.name}
                onError={() => setImgError(true)}
              />
            ) : (
              <AvatarPlaceholder>
                {card.name ? card.name.charAt(0) : <User size={20} />}
              </AvatarPlaceholder>
            )}
            <div>
              <CardName>{card.name}</CardName>
              <div tw="flex items-center gap-2">
                <StarsContainer>{renderStars(card.rating)}</StarsContainer>
                <CardDate>{card.date}</CardDate>
              </div>
            </div>
          </CardHeader>
          <CardText>"{card.text}"</CardText>
        </div>
        <div tw="text-[10px] text-gray-400 font-medium flex items-center gap-1 mt-2">
          <svg width="12" height="12" viewBox="0 0 24 24">
            <path
              fill="#4285f4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34a853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#fbbc04"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#ea4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Posted on Google
        </div>
      </Card>
    );
  };

  return (
    <>
      <style>{`
        @keyframes marqueeScroll {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      <Section>
        <HeadingContainer>
          <Subheading>Google reviews</Subheading>
          <Heading>
            What our students <br /> have to say
          </Heading>

          <Paragraph>
            Real feedback from our students on Google about their journey under{" "}
            <span tw="text-[#3f6c68] font-bold">CA Vijender Aggarwal</span>.
          </Paragraph>
        </HeadingContainer>

        {loading ? (
          <div tw="flex justify-center py-20">
            <Loader2 tw="animate-spin text-indigo-600" size={32} />
          </div>
        ) : reviews.length > 0 ? (
          <>
            <RowWrapper>
              <LeftFade />
              <MarqueeInner>
                {[...reviews, ...reviews, ...reviews].map((card, i) => (
                  <CreateCard key={i} card={card} />
                ))}
              </MarqueeInner>
              <RightFade />
            </RowWrapper>

            <RowWrapper>
              <LeftFade />
              <MarqueeInner reverse>
                {[...reviews, ...reviews, ...reviews].map((card, i) => (
                  <CreateCard key={i} card={card} />
                ))}
              </MarqueeInner>
              <RightFade />
            </RowWrapper>
          </>
        ) : (
          <div tw="text-center text-gray-400 py-10">Waiting for reviews...</div>
        )}
      </Section>
    </>
  );
};

export default TestimonialsSection;
